<div class="page-head">
  <div>
    <p class="eyebrow">งานอนุมัติ</p>
    <h1>อนุมัติการคืนเงิน</h1>
    <p>ตรวจสอบและพิจารณารายการคืนเงินของ Booking ก่อนส่งให้ฝ่ายบัญชีดำเนินการจ่าย</p>
  </div>
</div>

<section class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
  <p-table
    #refundTable
    [value]="rows()"
    dataKey="id"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [globalFilterFields]="[
      'refundNo',
      'booking.bookingNo',
      'booking.booking_customer.name',
      'booking.tourName',
      'namerefund',
      'bankName',
      'requestedBy.firstName',
      'requestedBy.lastName',
    ]"
    [rowHover]="true"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="แสดง {first} ถึง {last} จาก {totalRecords} รายการ"
    [tableStyle]="{ 'min-width': '1100px' }"
  >
    <ng-template #caption>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label class="relative block w-full max-w-md">
          <lucide-icon
            name="Search"
            [size]="17"
            class="pointer-events-none absolute top-1/2 left-2 z-10 -translate-y-1/2 text-slate-400"
          />
          <input
            pInputText
            #query
            class="h-11 w-full pr-3 text-sm placeholder:text-slate-400"
            style="padding-left: 2.75rem; border-radius: 0.5rem"
            placeholder="ค้นหาเลขที่คืนเงิน Booking ลูกค้า หรือผู้รับเงิน"
            (input)="refundTable.filterGlobal(query.value, 'contains')"
          />
        </label>
        <app-refresh-button
          class="self-end sm:self-auto"
          label="รีเฟรชรายการอนุมัติการคืนเงิน"
          [loading]="isLoadingRefunds()"
          (refresh)="loadRefunds()"
        />
      </div>
    </ng-template>

    <ng-template #header>
      <tr>
        <th class="w-14 text-center text-xs whitespace-nowrap">ลำดับ</th>
        <th pSortableColumn="refundNo" class="min-w-52 text-xs whitespace-nowrap">
          รายการคืนเงิน <p-sortIcon field="refundNo" />
        </th>
        <th pSortableColumn="booking.bookingNo" class="min-w-72 text-xs whitespace-nowrap">
          Booking / ลูกค้า <p-sortIcon field="booking.bookingNo" />
        </th>
        <th class="min-w-60 text-xs whitespace-nowrap">ผู้รับเงินคืน</th>
        <th pSortableColumn="amount" class="w-44 text-right text-xs whitespace-nowrap">
          จำนวนเงินคืน <p-sortIcon field="amount" />
        </th>
        <th pSortableColumn="status" class="w-44 text-xs whitespace-nowrap">
          สถานะ <p-sortIcon field="status" />
        </th>
        <th class="w-28 text-right text-xs whitespace-nowrap">พิจารณา</th>
      </tr>
    </ng-template>

    <ng-template #body let-row let-rowIndex="rowIndex">
      <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
        <td class="py-3.5 text-center text-[13px] text-slate-400 tabular-nums align-top">
          {{ rowIndex + 1 }}
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold text-sky-700">{{ row.refundNo }}</p>
          <p class="mt-1 mb-0 text-xs font-semibold text-violet-700">
            {{ row.refundType === 'FULL' ? 'คืนทั้งหมด' : 'คืนบางส่วน' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            ส่งเมื่อ {{ (row.requestedAt || row.createdAt) | date: 'dd MMM yyyy, HH:mm' }}
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold text-slate-800">{{ row.booking.bookingNo }}</p>
          <p class="mt-1 mb-0 max-w-72 text-xs leading-5 text-slate-500">
            {{ row.booking.tourName || 'ไม่ระบุโปรแกรมทัวร์' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            ลูกค้า: {{ row.booking.booking_customer.name || '—' }}
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold text-slate-800">{{ row.namerefund }}</p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            {{ row.bankName || 'ไม่ระบุธนาคาร' }} · {{ row.account_number }}
          </p>
        </td>
        <td class="py-3.5 text-right text-sm font-semibold whitespace-nowrap text-rose-700 tabular-nums align-top">
          {{ row.amount | number: '1.2-2' }} บาท
        </td>
        <td class="py-3.5 align-top"><app-status-badge [status]="row.status" /></td>
        <td class="py-3.5 align-top">
          <div class="flex justify-end gap-1.5">
            @if (row.attachmentPath) {
              <button
                type="button"
                class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
                title="เปิดเอกสารคืนเงิน"
                (click)="openAttachment(row.attachmentPath)"
              >
                <lucide-icon name="FileText" [size]="15" />
              </button>
            }
            <button
              type="button"
              class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
              title="ดูรายละเอียดและพิจารณา"
              (click)="open(row)"
            >
              <lucide-icon name="Eye" [size]="15" />
            </button>
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td colspan="7">
          <div class="empty">
            <b><lucide-icon name="Undo2" [size]="22" /></b>
            <h3>ไม่มีรายการคืนเงินรออนุมัติ</h3>
            <p>รายการใหม่จะแสดงที่นี่</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</section>

@if (selected(); as refund) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="close()"
  >
    <section
      class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            {{ refund.refundNo }}
          </p>
          <h2 class="m-0 text-xl font-bold text-slate-800">รายละเอียดการคืนเงิน</h2>
          <div class="mt-2"><app-status-badge [status]="refund.status" /></div>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          title="ปิด"
          (click)="close()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>

      <div class="max-h-[68vh] overflow-y-auto p-5 sm:p-6">
        <section>
          <h3 class="m-0 text-base font-bold text-slate-800">ข้อมูล Booking</h3>
          <dl class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt class="text-xs font-semibold text-slate-500">เลขที่ Booking</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.booking.bookingNo }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ลูกค้า</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.booking.booking_customer.name }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">โปรแกรมทัวร์</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.booking.tourName }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">วันเดินทาง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.booking.departureDate | date: 'dd MMM yyyy' }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">จำนวนผู้เดินทาง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.booking.pax | number }} คน</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ยอด Booking</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.booking.totalAmount | number: '1.2-2' }} บาท</dd>
            </div>
          </dl>
        </section>

        <section class="mt-6">
          <h3 class="m-0 text-base font-bold text-slate-800">ข้อมูลการคืนเงิน</h3>
          <dl class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
            <div>
              <dt class="text-xs font-semibold text-slate-500">ประเภทการคืน</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.refundType === 'FULL' ? 'คืนทั้งหมด' : 'คืนบางส่วน' }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">จำนวนเงินคืน</dt>
              <dd class="mt-1 mb-0 font-semibold text-rose-700">{{ refund.amount | number: '1.2-2' }} บาท</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ชื่อผู้รับเงินคืน</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.namerefund }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ธนาคาร / เลขบัญชี</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.bankName || '—' }} / {{ refund.account_number }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ผู้ส่งคำขอ</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ refund.requestedBy.firstName }} {{ refund.requestedBy.lastName }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">วันที่ส่ง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ (refund.requestedAt || refund.createdAt) | date: 'dd MMM yyyy, HH:mm' }}</dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-xs font-semibold text-slate-500">เหตุผลการคืนเงิน</dt>
              <dd class="mt-1 mb-0 whitespace-pre-line text-slate-800">{{ refund.reason || '—' }}</dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-xs font-semibold text-slate-500">เอกสารคืนเงิน</dt>
              <dd class="mt-1 mb-0">
                @if (refund.attachmentPath) {
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 font-semibold text-sky-700 hover:text-sky-900"
                    (click)="openAttachment(refund.attachmentPath)"
                  >
                    <lucide-icon name="FileText" [size]="16" /> เปิดเอกสารคืนเงิน
                    <lucide-icon name="ExternalLink" [size]="15" />
                  </button>
                } @else {
                  <span class="text-slate-400">ไม่มีเอกสาร</span>
                }
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
          (click)="close()"
        >
          ปิด
        </button>
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-5 text-[15px] font-semibold text-red-600 hover:bg-red-50"
          (click)="reject(refund)"
        >
          <lucide-icon name="X" [size]="17" /> ไม่อนุมัติ
        </button>
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center gap-2 rounded-xl bg-emerald-600 px-5 text-[15px] font-semibold text-white hover:bg-emerald-700"
          (click)="approve(refund)"
        >
          <lucide-icon name="Check" [size]="17" /> อนุมัติ
        </button>
      </div>
    </section>
  </div>
}
